import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ReactComponent as TikTok } from '../../assets/images/tiktok.svg';
import XIcon from '@mui/icons-material/X';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

const FOOTER_SECTIONS = {
  sitemap: {
    title: 'Sitemap',
    links: [
      { text: 'Pool Hall', href: '/pool-hall' },
      { text: 'Community Pools', href: '/community-pools' },
      { text: 'Contact Us', href: '/contact' }
    ]
  },
  resources: {
    title: 'Resources',
    links: [
      { text: 'Partners', href: '/partners' }
    ]
  },
  legal: {
    title: 'Legal',
    links: [
      {
        text: 'Terms of Service',
        href: 'https://poolpartyfilestorage.blob.core.windows.net/documents/Pool_Party_Terms_of_Service_2024.pdf',
        external: true
      },
      {
        text: 'Privacy Policy',
        href: 'https://poolpartyfilestorage.blob.core.windows.net/documents/Pool_Party_LLC_Privacy_Policy_2024.pdf',
        external: true
      }
    ]
  }
};

const SOCIAL_LINKS = [
  {
    id: 'instagram',
    icon: <InstagramIcon sx={{ fontSize: '20px' }} />,
    href: 'https://www.instagram.com/poolpartygaming'
  },
  {
    id: 'facebook',
    icon: <FacebookIcon sx={{ fontSize: '20px' }} />,
    href: 'https://www.facebook.com/people/Pool-Party-Gaming/61561675242688/?_rdr'
  },
  {
    id: 'twitter',
    icon: <XIcon sx={{ fontSize: '20px' }} />,
    href: 'https://twitter.com/poolpartygaming'
  },
  {
    id: 'youtube',
    icon: <YouTubeIcon sx={{ fontSize: '20px' }} />,
    href: 'https://www.youtube.com/@Pool_Party_Gaming'
  },
  {
    id: 'tiktok',
    icon: <TikTok style={{ width: '20px', height: '20px', fill: '#333' }} />,
    href: 'https://www.tiktok.com/@poolpartygaming'
  },
  {
    id: 'linkedin',
    icon: <LinkedInIcon sx={{ fontSize: '20px' }} />,
    href: 'https://www.linkedin.com/company/pool-party-gaming'
  }
];

const FooterSection = ({ title, links }) => (
  <Box sx={{ mb: { xs: 4, md: 0 }, textAlign: 'left' }}>
    <Typography
      sx={{
        color: '#fff',
        fontSize: '18px',
        fontWeight: '600',
        mb: 2,
        fontFamily: 'Inter',
        textAlign: 'left'
      }}
    >
      {title}
    </Typography>
    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, textAlign: 'left' }}>
      {links.map((link, index) => (
        <li key={index} style={{ marginBottom: '12px', textAlign: 'left' }}>
          <Box
            component="a"
            href={link.href}
            target={link.external ? '_blank' : '_self'}
            rel={link.external ? 'noopener noreferrer' : undefined}
            sx={{
              color: '#ccc',
              fontSize: '14px',
              textDecoration: 'none',
              fontFamily: 'Inter',
              textAlign: 'left',
              display: 'block',
              '&:hover': {
                color: '#fff',
                textDecoration: 'underline'
              }
            }}
          >
            {link.text}
          </Box>
        </li>
      ))}
    </Box>
  </Box>
);

const SocialIcon = ({ icon, href }) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      backgroundColor: '#fff',
      color: '#333',
      textDecoration: 'none',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: '#f0f0f0',
        transform: 'translateY(-2px)'
      }
    }}
  >
    {icon}
  </Box>
);

export default function Footer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  //console.log("Footer isMobile:", isMobile);

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#1A1C1E',
        color: '#fff',
        pt: { xs: 4, md: 6 },
        pb: { xs: 2, md: 3 },
        px: { xs: 2, md: 4 }
      }}
    >
      <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
        {isMobile ? (
          // Mobile Layout - Stacked sections
          <Box sx={{
            maxWidth: '300px',
            margin: '0 auto'
          }}>
            <FooterSection
              title={FOOTER_SECTIONS.sitemap.title}
              links={FOOTER_SECTIONS.sitemap.links}
            />
            <FooterSection
              title={FOOTER_SECTIONS.resources.title}
              links={FOOTER_SECTIONS.resources.links}
            />
            <FooterSection
              title={FOOTER_SECTIONS.legal.title}
              links={FOOTER_SECTIONS.legal.links}
            />

            {/* Social Icons */}
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 2,
                  maxWidth: '120px'
                }}
              >
                {SOCIAL_LINKS.map((social) => (
                  <SocialIcon
                    key={social.id}
                    icon={social.icon}
                    href={social.href}
                  />
                ))}
              </Box>
            </Box>

            {/* SportsData Logo */}
            <Box sx={{ mb: 3 }}>
              <a href="https://sportsdata.io" target="_blank" rel="noopener noreferrer">
                <img
                  src="https://sportsdata.io/assets/images/badges/sportsdataio_light_ss_300.png"
                  alt="Powered by SportsDataIO"
                  style={{ maxWidth: '120px', height: 'auto', display: 'block' }}
                />
              </a>
            </Box>
          </Box>
        ) : (
          // Desktop Layout - Three columns
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
            {/* Left side - Navigation columns */}
            <Box sx={{ display: 'flex', gap: 8 }}>
              <FooterSection
                title={FOOTER_SECTIONS.sitemap.title}
                links={FOOTER_SECTIONS.sitemap.links}
              />
              <FooterSection
                title={FOOTER_SECTIONS.resources.title}
                links={FOOTER_SECTIONS.resources.links}
              />
              <FooterSection
                title={FOOTER_SECTIONS.legal.title}
                links={FOOTER_SECTIONS.legal.links}
              />
            </Box>

            {/* Right side - Social and SportsData */}
            <Box sx={{ textAlign: 'right' }}>
              <Box sx={{ mb: 3 }}>
                <a href="https://sportsdata.io" target="_blank" rel="noopener noreferrer">
                  <img
                    src="https://sportsdata.io/assets/images/badges/sportsdataio_light_ss_300.png"
                    alt="Powered by SportsDataIO"
                    style={{ maxWidth: '150px', height: 'auto' }}
                  />
                </a>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gridTemplateRows: 'repeat(2, 1fr)',
                  gap: 2.5,
                  justifyContent: 'flex-end',
                  width: '140px'
                }}
              >
                {SOCIAL_LINKS.map((social) => (
                  <SocialIcon
                    key={social.id}
                    icon={social.icon}
                    href={social.href}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* Copyright - Always at bottom center */}
        <Box
          sx={{
            pt: 2,
            textAlign: 'center'
          }}
        >
          <Typography
            sx={{
              color: '#ccc',
              fontSize: '14px',
              fontFamily: 'Inter',
              fontWeight: '400'
            }}
          >
            ©Pool Party Gaming, LLC - 2024
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}